/**
 * Composant générique pour rendre les paramètres dynamiquement
 * Basé sur le fichier de configuration settings.config.json
 */

import { useState, useEffect, ReactNode } from 'react';
import { 
  Cog6ToothIcon, 
  BellIcon, 
  ShieldCheckIcon, 
  GlobeAltIcon, 
  CreditCardIcon, 
  UserCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
// Import du fichier de configuration
import settingsConfigData from '@/config/settings.config.json';
import { settingsService } from '@/services/settings.service';
import { rolesService, Role } from '@/services/roles.service';
import { useAuth } from '@/context/AuthContext';
import '@/styles/settings.css';

// Section Stripe (toujours incluse pour la configuration des paiements)
const STRIPE_SECTION: SettingSection = {
  sectionId: 'stripe',
  sectionLabel: 'Configuration Stripe',
  sectionIcon: 'CreditCardIcon',
  description: 'Saisissez les clés Stripe et vérifiez le statut de configuration',
  options: [
    {
      id: 'stripe_publishable_key',
      label: 'Publishable Key',
      type: 'password',
      default: '',
      required: false,
      description: 'Clé publique Stripe (ex: pk_test_...)',
      placeholder: 'pk_test_...',
    },
    {
      id: 'stripe_secret_key',
      label: 'Secret Key',
      type: 'password',
      default: '',
      required: false,
      description: 'Clé secrète Stripe (ex: sk_test_...)',
      placeholder: 'sk_test_...',
    },
  ],
};

// Vérifier que la configuration est valide et inclure Stripe si absent
let settingsConfig: SettingSection[] = [];
try {
  const raw = (settingsConfigData as any)?.default ?? settingsConfigData;
  const parsed = Array.isArray(raw) ? raw : [];
  const hasStripe = parsed.some((s: SettingSection) => s.sectionId === 'stripe');
  settingsConfig = hasStripe ? parsed : [STRIPE_SECTION, ...parsed];
  if (settingsConfig.length === 0) {
    settingsConfig = [STRIPE_SECTION];
  }
  if (!settingsConfig.some((s) => s.sectionId === 'stripe')) {
    settingsConfig = [STRIPE_SECTION, ...settingsConfig];
  }
} catch (error) {
  console.error('❌ Erreur chargement configuration paramètres:', error);
  settingsConfig = [STRIPE_SECTION];
}

interface SettingOption {
  id: string;
  label: string;
  type: string;
  default?: any;
  required?: boolean;
  description?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  fields?: SettingOption[];
  conditional?: {
    field: string;
    value: any;
  };
  [key: string]: any;
}

interface SettingSection {
  sectionId: string;
  sectionLabel: string;
  sectionIcon?: string;
  description?: string;
  options: SettingOption[];
}

interface SettingsFormProps {
  initialValues?: Record<string, any>;
  onSubmit?: (values: Record<string, any>) => Promise<void>;
  onSave?: (sectionId: string, values: Record<string, any>) => Promise<void>;
}

const shouldIgnoreLegacyStripeOption = (sectionId: string, optionId: string) => {
  // Évite les collisions entre l'ancienne config Stripe dans "payment"
  // et la section dédiée "stripe" (mêmes ids de champs).
  return sectionId === 'payment' && optionId === 'stripe_config';
};

const iconMap: Record<string, any> = {
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  CreditCardIcon,
  UserCircleIcon,
};

export const SettingsForm = ({ 
  initialValues = {}, 
  onSubmit: _onSubmit,
  onSave 
}: SettingsFormProps) => {
  const { user, loading: authLoading } = useAuth();
  const [activeSection, setActiveSection] = useState<string>('general');
  const [userRole, setUserRole] = useState<Role | null>(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Charger les paramètres depuis l'API
    const loadSettings = async () => {
      if (!settingsConfig || settingsConfig.length === 0) {
        setLoadingSettings(false);
        return;
      }

      setLoadingSettings(true);
      try {
        const savedSettings = await settingsService.getAll();
        // Initialiser avec les valeurs par défaut puis surcharger avec les valeurs sauvegardées
        const defaults: Record<string, any> = {};
        settingsConfig.forEach((section: SettingSection) => {
          section.options.forEach((option) => {
            if (shouldIgnoreLegacyStripeOption(section.sectionId, option.id)) {
              return;
            }
            if (option.default !== undefined) {
              defaults[option.id] = option.default;
            }
            if (option.fields) {
              option.fields.forEach((field) => {
                if (field.default !== undefined) {
                  defaults[field.id] = field.default;
                }
              });
            }
          });
        });
        const normalizedSaved: Record<string, any> = {};
        settingsConfig.forEach((section: SettingSection) => {
          section.options.forEach((option) => {
            if (shouldIgnoreLegacyStripeOption(section.sectionId, option.id)) {
              return;
            }
            if (savedSettings[option.id] !== undefined) {
              normalizedSaved[option.id] = savedSettings[option.id];
            } else if (savedSettings[`${section.sectionId}.${option.id}`] !== undefined) {
              normalizedSaved[option.id] = savedSettings[`${section.sectionId}.${option.id}`];
            }
            if (option.fields) {
              option.fields.forEach((field) => {
                if (savedSettings[field.id] !== undefined) {
                  normalizedSaved[field.id] = savedSettings[field.id];
                } else if (savedSettings[`${section.sectionId}.${field.id}`] !== undefined) {
                  normalizedSaved[field.id] = savedSettings[`${section.sectionId}.${field.id}`];
                }
              });
            }
          });
        });

        setValues({ ...defaults, ...normalizedSaved, ...initialValues });
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
        // En cas d'erreur, utiliser les valeurs par défaut
        const defaults: Record<string, any> = {};
        settingsConfig.forEach((section: SettingSection) => {
          section.options.forEach((option) => {
            if (shouldIgnoreLegacyStripeOption(section.sectionId, option.id)) {
              return;
            }
            if (option.default !== undefined) {
              defaults[option.id] = option.default;
            }
            if (option.fields) {
              option.fields.forEach((field) => {
                if (field.default !== undefined) {
                  defaults[field.id] = field.default;
                }
              });
            }
          });
        });
        setValues({ ...defaults, ...initialValues });
      } finally {
        setLoadingSettings(false);
      }
    };
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Récupérer le rôle de l'utilisateur
  useEffect(() => {
    const loadUserRole = async () => {
      if (user?.role_id) {
        setLoadingRole(true);
        try {
          const role = await rolesService.getById(user.role_id);
          setUserRole(role);
        } catch (error) {
          console.error('Erreur lors de la récupération du rôle:', error);
          setUserRole(null);
        } finally {
          setLoadingRole(false);
        }
      } else {
        setUserRole(null);
      }
    };

    loadUserRole();
  }, [user?.role_id]);

  const handleChange = (id: string, value: any) => {
    console.log('🔄 Changement:', id, '=', value);
    setValues((prev) => {
      const newValues = { ...prev, [id]: value };
      console.log('📝 Nouvelles valeurs:', newValues);
      return newValues;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
    setSaved(false);
  };

  const validateField = (option: SettingOption, value: any): string | null => {
    if (option.required && (!value || value === '')) {
      return `${option.label} est requis`;
    }

    if (option.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Format d\'email invalide';
    }

    if (option.type === 'url' && value && !/^https?:\/\/.+/.test(value)) {
      return 'Format d\'URL invalide (doit commencer par http:// ou https://)';
    }

    if (option.minLength && value && value.length < option.minLength) {
      return `Minimum ${option.minLength} caractères requis`;
    }

    if (option.maxLength && value && value.length > option.maxLength) {
      return `Maximum ${option.maxLength} caractères autorisés`;
    }

    if (option.pattern && value && !new RegExp(option.pattern).test(value)) {
      return option.description || 'Format invalide';
    }

    return null;
  };

  const validateSection = (section: SettingSection): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    section.options.forEach((option) => {
      // Vérifier les conditions
      if (option.conditional) {
        const conditionValue = values[option.conditional.field];
        if (conditionValue !== option.conditional.value) {
          return; // Skip si condition non remplie
        }
      }

      const value = values[option.id];
      const error = validateField(option, value);
      if (error) {
        newErrors[option.id] = error;
        isValid = false;
      }

      // Valider les sous-champs
      if (option.fields) {
        option.fields.forEach((field) => {
          const fieldValue = values[field.id];
          const fieldError = validateField(field, fieldValue);
          if (fieldError) {
            newErrors[field.id] = fieldError;
            isValid = false;
          }
        });
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async (sectionId: string) => {
    console.log('💾 Sauvegarde de la section:', sectionId);
    const section = settingsConfig.find((s: SettingSection) => s.sectionId === sectionId);
    if (!section) {
      console.error('❌ Section non trouvée:', sectionId);
      return;
    }

    if (!validateSection(section)) {
      console.warn('⚠️ Validation échouée pour la section:', sectionId);
      return;
    }

    if (sectionId === 'stripe') {
      const sk = String(values.stripe_secret_key || '').trim();
      const pk = String(values.stripe_publishable_key || '').trim();
      if ((sk && (sk.includes('xxxx') || sk.length < 30)) || (pk && (pk.includes('xxxx') || pk.length < 30))) {
        if (!window.confirm('Les clés semblent être des placeholders (ex: xxxx). Utilisez des clés réelles depuis dashboard.stripe.com pour que le paiement fonctionne. Enregistrer quand même ?')) {
          return;
        }
      }
    }

    setLoading(true);
    try {
      const sectionValues: Record<string, any> = {};
      section.options.forEach((option) => {
        if (shouldIgnoreLegacyStripeOption(section.sectionId, option.id)) {
          return;
        }
        if (values[option.id] !== undefined) {
          sectionValues[option.id] = values[option.id];
        }
        if (option.fields) {
          option.fields.forEach((field) => {
            if (values[field.id] !== undefined) {
              sectionValues[field.id] = values[field.id];
            }
          });
        }
      });

      console.log('📤 Envoi des valeurs:', sectionValues);
      // Sauvegarder via le service
      await settingsService.saveSection(sectionId, sectionValues);
      console.log('✅ Sauvegarde réussie');
      
      if (onSave) {
        await onSave(sectionId, sectionValues);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la sauvegarde';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isStripeConfigured = () => {
    const publishableKey = String(values.stripe_publishable_key || '').trim();
    const secretKey = String(values.stripe_secret_key || '').trim();
    return publishableKey.length > 0 && secretKey.length > 0;
  };

  const renderField = (option: SettingOption, parentId?: string): ReactNode => {
    const fieldId = parentId ? `${parentId}.${option.id}` : option.id;
    const inputId = `setting-${fieldId.replace(/\./g, '-')}`;
    const value = values[option.id];
    const error = errors[option.id];

    // Vérifier les conditions
    if (option.conditional) {
      const conditionValue = values[option.conditional.field];
      if (conditionValue !== option.conditional.value) {
        return null;
      }
    }

    switch (option.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <input
              id={inputId}
              type={option.type}
              className={`form-input ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => {
                e.stopPropagation();
                handleChange(option.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder={option.placeholder}
              maxLength={option.maxLength}
              required={option.required}
            />
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'password':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <div className="form-password-wrapper">
              <input
                id={inputId}
                type={visiblePasswords[fieldId] ? 'text' : 'password'}
                className={`form-input ${error ? 'error' : ''}`}
                value={value || ''}
                onChange={(e) => handleChange(option.id, e.target.value)}
                placeholder={option.placeholder}
                required={option.required}
              />
              <button
                type="button"
                className="form-password-toggle"
                onClick={() => {
                  setVisiblePasswords((prev) => ({
                    ...prev,
                    [fieldId]: !prev[fieldId],
                  }));
                }}
                aria-label={visiblePasswords[fieldId] ? 'Masquer la clé' : 'Afficher la clé'}
              >
                {visiblePasswords[fieldId] ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </div>
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <textarea
              id={inputId}
              className={`form-textarea ${error ? 'error' : ''}`}
              value={value || ''}
              onChange={(e) => {
                e.stopPropagation();
                handleChange(option.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder={option.placeholder}
              rows={option.rows || 4}
              maxLength={option.maxLength}
              required={option.required}
            />
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'number':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <input
              id={inputId}
              type="number"
              className={`form-input ${error ? 'error' : ''}`}
              value={value || option.default || ''}
              onChange={(e) => handleChange(option.id, parseFloat(e.target.value) || 0)}
              min={option.min}
              max={option.max}
              step={option.step}
              required={option.required}
            />
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <select
              id={inputId}
              className={`form-select ${error ? 'error' : ''}`}
              value={value || option.default || ''}
              onChange={(e) => {
                e.stopPropagation();
                handleChange(option.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              required={option.required}
            >
              {option.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'multiselect':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <select
              id={inputId}
              multiple
              className={`form-select ${error ? 'error' : ''}`}
              value={Array.isArray(value) ? value : []}
              onChange={(e) => {
                e.stopPropagation();
                const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
                handleChange(option.id, selected);
              }}
              onClick={(e) => e.stopPropagation()}
              required={option.required}
            >
              {option.options?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'toggle':
        const isChecked = value === true || value === 'true' || value === 1 || value === '1';
        return (
          <div key={fieldId} className="form-group">
            <div className="form-toggle-wrapper">
              <label className="form-label-toggle">
                <input
                  type="checkbox"
                  className="form-toggle-input"
                  checked={isChecked}
                  onChange={(e) => handleChange(option.id, e.target.checked)}
                />
                <span className="form-toggle-slider"></span>
                <span className="form-toggle-label">{option.label}</span>
              </label>
            </div>
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={fieldId} className="form-group">
            <label className="form-label" htmlFor={inputId}>
              {option.label}
              {option.required && <span className="required">*</span>}
            </label>
            <input
              id={inputId}
              type="file"
              className="form-input"
              accept={option.accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (option.maxSize && file.size > option.maxSize) {
                    setErrors((prev) => ({
                      ...prev,
                      [option.id]: `Fichier trop volumineux (max ${option.maxSize / 1024 / 1024}MB)`,
                    }));
                    return;
                  }
                  handleChange(option.id, file);
                }
              }}
            />
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
            {error && <div className="form-error">{error}</div>}
          </div>
        );

      case 'group':
        return (
          <div key={fieldId} className="form-group form-group-nested">
            <h4 className="form-group-title">{option.label}</h4>
            {option.description && (
              <div className="form-group-description">{option.description}</div>
            )}
            {option.fields?.map((field) => renderField(field, option.id))}
          </div>
        );

      case 'button':
        return (
          <div key={fieldId} className="form-group">
            <button
              type="button"
              className={`btn ${option.variant === 'danger' ? 'btn-error' : option.variant === 'warning' ? 'btn-warning' : 'btn-secondary'}`}
              onClick={async () => {
                if (option.confirm) {
                  const confirmed = option.requiresText
                    ? prompt(option.confirmMessage || 'Confirmer', '') === option.requiresText
                    : window.confirm(option.confirmMessage || 'Êtes-vous sûr ?');
                  if (!confirmed) return;
                }
                // Appeler l'action via le service
                try {
                  setLoading(true);
                  const result = await settingsService.executeAction(option.action || '', {
                    confirmText: option.requiresText,
                    ...values
                  });
                  alert(result.message || 'Action exécutée avec succès');
                  if (option.action === 'delete_account' || option.action === 'logout_all') {
                    // Rediriger après certaines actions
                    window.location.href = '/';
                  }
                } catch (error: any) {
                  const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'exécution de l\'action';
                  alert(errorMessage);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {option.label}
            </button>
            {option.description && (
              <div className="form-help">{option.description}</div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const currentSection = settingsConfig.find(
    (s: SettingSection) => s.sectionId === activeSection
  );

  // Debug: Vérifier que le composant est bien rendu
  console.log('🔍 SettingsForm rendu:', {
    activeSection,
    currentSection: currentSection?.sectionLabel,
    valuesCount: Object.keys(values).length,
    settingsConfigLength: settingsConfig.length
  });

  if (!settingsConfig || settingsConfig.length === 0) {
    return (
      <div className="settings-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Erreur: Configuration des paramètres non chargée</p>
          <button onClick={() => window.location.reload()}>Recharger la page</button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', zIndex: 1 }}>
      {/* Sidebar Navigation */}
      <aside className="settings-sidebar">
        <h2 className="settings-sidebar-title">Paramètres</h2>
        <nav className="settings-nav">
          {settingsConfig.map((section: SettingSection) => {
            const Icon = section.sectionIcon ? iconMap[section.sectionIcon] : Cog6ToothIcon;
            return (
              <button
                key={section.sectionId}
                className={`settings-nav-item ${activeSection === section.sectionId ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🖱️ Clic sur section:', section.sectionId);
                  setActiveSection(section.sectionId);
                }}
                type="button"
              >
                {Icon && <Icon className="settings-nav-icon" />}
                <span>{section.sectionLabel}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="settings-main">
        {currentSection && (
          <>
            <div className="settings-header">
              <h1 className="settings-title">{currentSection.sectionLabel}</h1>
              {currentSection.description && (
                <p className="settings-description">{currentSection.description}</p>
              )}
              {activeSection === 'stripe' && (
                <div className={`stripe-status-badge ${isStripeConfigured() ? 'connected' : 'not-configured'}`}>
                  {isStripeConfigured() ? 'Stripe connecté' : 'Stripe non configuré'}
                </div>
              )}
            </div>

            {activeSection === 'account' ? (
              /* Section Compte - Affichage des données du compte connecté */
              <div className="settings-account-info">
                {authLoading || loadingSettings ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <p>Chargement des informations du compte...</p>
                  </div>
                ) : (
                  <>
                    <div className="account-info-card">
                      <h3 className="account-info-title">Informations du compte</h3>
                      <div className="account-info-grid">
                        <div className="account-info-item">
                          <label className="account-info-label">Nom complet</label>
                          <div className="account-info-value">{user?.fullName || 'Non défini'}</div>
                        </div>
                        <div className="account-info-item">
                          <label className="account-info-label">Email</label>
                          <div className="account-info-value">{user?.email || 'Non défini'}</div>
                        </div>
                        <div className="account-info-item">
                          <label className="account-info-label">Rôle</label>
                          <div className="account-info-value">
                            {loadingRole ? (
                              <span style={{ color: '#999' }}>Chargement...</span>
                            ) : userRole ? (
                              <span style={{ 
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                backgroundColor: '#f0f0f0',
                                color: '#8c1843',
                                fontWeight: 600,
                                fontSize: '14px'
                              }}>
                                {userRole.name}
                              </span>
                            ) : user?.role_id ? (
                              <span style={{ color: '#999' }}>Rôle ID: {user.role_id}</span>
                            ) : (
                              <span style={{ color: '#999' }}>Aucun rôle assigné</span>
                            )}
                          </div>
                        </div>
                        <div className="account-info-item">
                          <label className="account-info-label">ID utilisateur</label>
                          <div className="account-info-value">#{user?.id || 'N/A'}</div>
                        </div>
                        {user?.createdAt && (
                          <div className="account-info-item">
                            <label className="account-info-label">Date de création</label>
                            <div className="account-info-value">
                              {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="account-info-note">
                      <p>Ces informations sont récupérées depuis votre session active. Pour modifier vos informations personnelles, contactez un administrateur.</p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              loadingSettings ? (
                <div className="settings-form" style={{ padding: '40px', textAlign: 'center' }}>
                  <p>Chargement des paramètres...</p>
                </div>
              ) : (
                <form
                  className="settings-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('📝 Formulaire soumis pour section:', activeSection);
                    handleSave(activeSection);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {currentSection.options
                    .filter((option) => !shouldIgnoreLegacyStripeOption(currentSection.sectionId, option.id))
                    .map((option) => renderField(option))}

                  <div className="settings-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading || loadingSettings}
                    >
                      {loading ? 'Enregistrement...' : activeSection === 'stripe' ? 'Enregistrer' : 'Enregistrer les modifications'}
                    </button>
                    {saved && (
                      <span className="settings-saved">✓ Paramètres enregistrés</span>
                    )}
                  </div>
                </form>
              )
            )}
          </>
        )}
      </main>
    </div>
  );
};
